import { css } from "emotion";
import { normalizeCSSClass, DesignTheme, Z_INDEX } from "@certa/blocks";
import { StudioLogo } from "@certa/blocks/thanos";
import { Stack } from "@certa/catalyst/layouts/Stack";
import { Alert } from "@certa/catalyst/components/Alert";
import { usePlatformSettingsProcessID } from "@certa/queries/hooks/appBuilder.hooks";
import { ImpersonationBanner } from "../ImpersonationBanner";
import { CreateCertaSupportDropdown } from "../HeaderHelpDropdown";
import { UserProfile } from "../components";
import { useScreenResolution } from "@certa/common/hooks/useScreenResolution";
import { useCanSeeCreateSupportButton } from "@certa/common/hooks/useCanSeeCreateSupportButton";
import { useShowHeaderDropdownOptions } from "@certa/common/hooks/useShowHeaderDropdownOptions";
import { useHeaderBanner } from "@certa/common/hooks/useHeaderBanner";
import { CatalystColors } from "@certa/catalyst/constants/styles";
import { Breadcrumb } from "../../Breadcrumb/Breadcrumb";
import { useLocation, Link } from "react-router";
import { useStudioPackage } from "@certa/queries/hooks/studio.hook";
import { getBreadcrumbLinks } from "./StudioHeaderV2.utils";
import { useStudioURLParams } from "../../../../studioCommon/src/hooks";
import { getStudioGenericRoute } from "../../../utils/routes";
import { useAuth } from "@certa/auth/AuthenticationProvider";

type StudioHeaderV2Props = {
  shouldShowUserProfile?: boolean;
};

export const StudioHeaderV2 = ({
  shouldShowUserProfile = true
}: StudioHeaderV2Props) => {
  const location = useLocation();

  const { user } = useAuth();
  const userName =
    (user?.first_name && user?.last_name
      ? `${user.first_name} ${user.last_name}`
      : user?.email) ?? "Anonymous";

  const impersonateId = sessionStorage.getItem("impersonateeId");
  // This is to prevent banner flicker
  // when logged in user becomes the impersonate for a split second
  // while the impersonate details are being fetched from the server
  const isSameUserImpersonation =
    (impersonateId && parseInt(impersonateId)) === user?.id;

  // TODO: Remove once Platform Settings UI (VET-34577) is done
  // Prefetches platform settings process ID
  usePlatformSettingsProcessID();

  const showHeaderDropdownOptions = useShowHeaderDropdownOptions();
  const { supportTicketData } = useCanSeeCreateSupportButton();

  const { isMobileResolution } = useScreenResolution();
  const bannerConfig = useHeaderBanner();

  const isBannerVisible = !!bannerConfig && !isMobileResolution;
  const shouldShowCertaSupportDropdown = !!(
    !isMobileResolution &&
    (showHeaderDropdownOptions.length || supportTicketData)
  );

  const { savepointId, branchId, appId } = useStudioURLParams();

  const { data: app } = useStudioPackage(appId);

  const breadcrumbLinks = getBreadcrumbLinks({
    currentPath: location.pathname,
    appName: app?.name,
    appId,
    branchId,
    savepointId
  });

  return (
    <DesignTheme
      className={normalizeCSSClass}
      style={{
        width: "100%",
        position: "sticky",
        top: 0,
        left: 0,
        right: 0,
        zIndex: Z_INDEX["zindex-fixed"]
      }}
    >
      {impersonateId && !isSameUserImpersonation && <ImpersonationBanner />}

      {isBannerVisible && (
        <Alert
          type={bannerConfig.type}
          message={bannerConfig.message}
          hasIcon={bannerConfig.hasIcon}
          width="100%"
        />
      )}
      <Stack
        direction="horizontal"
        justify="space-between"
        align="center"
        padding="8 12"
        width="100%"
        backgroundColor={CatalystColors.NEUTRAL_100}
        dangerouslySetClassName={headerStyles}
      >
        <Stack direction="horizontal" gap="16" align="center" height="100%">
          <Link to={getStudioGenericRoute()}>
            <StudioLogo width={66.67} height={16} />
          </Link>
          <div className={verticalDividerClassNames}></div>
          <Breadcrumb
            links={breadcrumbLinks}
            dangerouslySetClassNames={breadcrumbClassNames}
          />
        </Stack>
        <Stack
          direction="horizontal"
          gap="32"
          align="center"
          justify="flex-end"
          width="100%"
        >
          {shouldShowCertaSupportDropdown && (
            <CreateCertaSupportDropdown
              supportTicketData={supportTicketData}
              dropdownOptions={showHeaderDropdownOptions}
            />
          )}
          {shouldShowUserProfile && <UserProfile userName={userName} />}
        </Stack>
      </Stack>
    </DesignTheme>
  );
};

const headerStyles = css({
  borderBottom: `1px solid ${CatalystColors.NEUTRAL_500}`
});

const breadcrumbClassNames = {
  container: css({
    gap: "8px !important"
  })
};

const verticalDividerClassNames = css({
  borderLeft: `1px solid ${CatalystColors.NEUTRAL_500}`,
  height: "16px"
});
